import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Typography } from '@mui/material';
import { COLOR_TOKENS } from '../designSystem/colors/colors';
import { AppIcon } from '../designSystem/icons/AppIcon';
var MAX_LEFT_IMAGE_DIMENSION = 232;
var HIGHLIGHTED_CTA_BACKGROUND_TYPE = {
    GRID: 'GRID',
    LINED: 'LINED',
    SOLID: 'SOLID',
};
export var HighlightedCTACard = function (_a) {
    var leftImageUrl = _a.leftImageUrl, title = _a.title, description = _a.description, ctaText = _a.ctaText, _b = _a.backgroundType, backgroundType = _b === void 0 ? { type: HIGHLIGHTED_CTA_BACKGROUND_TYPE.GRID } : _b, ctaOnClick = _a.ctaOnClick;
    var backgroundStyleToUse = backgroundTypeToStyleMap[backgroundType.type];
    var backgroundColor = backgroundType.type === HIGHLIGHTED_CTA_BACKGROUND_TYPE.SOLID
        ? backgroundType.backgroundColor
        : undefined;
    return (_jsxs(Box, { sx: containerSx(backgroundColor), children: [_jsx(Box, { component: "img", src: leftImageUrl, sx: leftImageSx }), _jsxs(Box, { sx: backgroundStyleToUse, children: [_jsxs(Box, { sx: titleButtonContainerSx, children: [_jsx(Typography, { variant: "display-xs-bold", children: title }), _jsx(Button, { id: "dashboard-build-blueprint-button", hierarchy: "Primary", size: "lg", onClick: ctaOnClick, endIcon: _jsx(AppIcon, { iconName: "ArrowRight", diameter: "20px", color: "Colors/Base/White" }), sx: buttonSx, children: ctaText })] }), _jsx(Box, { sx: descriptionSx, children: _jsx(Typography, { variant: "text-sm-regular", children: description }) })] })] }));
};
var containerSx = function (backgroundColor) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            display: 'flex',
            flexDirection: {
                xs: 'column',
                sm: 'row',
            },
            borderRadius: backrs.radius['radius-xl'],
            overflow: 'hidden',
            border: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
            backgroundColor: backgroundColor || backrs.colors['Colors/Base/White'],
        });
    };
};
var leftImageSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        maxWidth: {
            sm: MAX_LEFT_IMAGE_DIMENSION,
        },
        maxHeight: {
            xs: MAX_LEFT_IMAGE_DIMENSION / 1.75,
            sm: 'unset',
        },
        borderRight: {
            sm: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        },
        borderBottom: {
            xs: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
            sm: 'none',
        },
        height: 'auto',
        borderTopLeftRadius: backrs.radius['radius-xl'],
        borderBottomLeftRadius: { md: backrs.radius['radius-xl'] },
        backgroundColor: backrs.colors['Colors/Background/bg-brand-primary'],
        pr: backrs.spacing['spacing-3xl'],
        pl: backrs.spacing['spacing-5xl'],
        py: {
            xs: backrs.spacing['spacing-3xl'],
            sm: backrs.spacing['spacing-6xl'],
        },
    });
};
var titleButtonContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: {
            xs: 'column-reverse',
            md: 'row',
        },
        justifyContent: 'space-between',
        alignItems: {
            xs: 'flex-start',
            md: 'center',
        },
        mb: backrs.spacing['spacing-2xl'],
        gap: backrs.spacing['spacing-xl'],
    });
};
var descriptionSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        mr: { md: backrs.spacing['spacing-9xl'] },
    });
};
var buttonSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        minWidth: 'fit-content',
        marginLeft: {
            md: backrs.spacing['spacing-4xl'],
        },
        alignSelf: {
            xs: 'flex-start',
            sm: 'flex-end',
            md: 'flex-start',
        },
    });
};
var HORIZONTAL_FADE_PERCENTAGE = 40;
var VERTICAL_FADE_PERCENTAGE = 85;
var CENTER_OPACITY = 0.35;
var GRID_SPACING_X = 32;
var GRID_SPACING_Y = 48;
var GRID_COLOR = COLOR_TOKENS['Brand/500'];
var FADE_HORIZONTAL_POSITION = 50;
var FADE_START_PERCENTAGE = 0;
var FADE_END_PERCENTAGE = 65;
var SHIFT_UP = 8;
var contentContainerCommon = function (_a) {
    var backrs = _a.backrs;
    return ({
        position: 'relative',
        borderRadius: backrs.radius['radius-xl'],
        overflow: 'hidden',
        p: backrs.spacing['spacing-3xl'],
    });
};
var gridBackground = function (_a) {
    var backrs = _a.backrs;
    return ({
        position: 'relative',
        borderRadius: backrs.radius['radius-xl'],
        overflow: 'hidden',
        p: backrs.spacing['spacing-3xl'],
        '&::before': {
            content: '""',
            position: 'absolute',
            top: -SHIFT_UP,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "\n      linear-gradient(".concat(GRID_COLOR, " 1px, transparent 1px),\n      linear-gradient(to right, ").concat(GRID_COLOR, " 1px, transparent 1px)\n    "),
            backgroundSize: "".concat(GRID_SPACING_X, "px ").concat(GRID_SPACING_Y, "px"),
            backgroundPosition: 'center',
            zIndex: 0,
            pointerEvents: 'none',
            WebkitMaskImage: "\n      radial-gradient(\n        ".concat(HORIZONTAL_FADE_PERCENTAGE * 2, "% ").concat(VERTICAL_FADE_PERCENTAGE * 2, "% at ").concat(FADE_HORIZONTAL_POSITION, "% 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") ").concat(FADE_START_PERCENTAGE, "%,\n        rgba(0, 0, 0, 0) ").concat(FADE_END_PERCENTAGE, "%\n      )\n    "),
            maskImage: "\n      radial-gradient(\n        ".concat(HORIZONTAL_FADE_PERCENTAGE * 2, "% ").concat(VERTICAL_FADE_PERCENTAGE * 2, "% at ").concat(FADE_HORIZONTAL_POSITION, "% 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") ").concat(FADE_START_PERCENTAGE, "%,\n        rgba(0, 0, 0, 0) ").concat(FADE_END_PERCENTAGE, "%\n      )\n    "),
        },
        zIndex: 1,
    });
};
var linedBackground = function (_a) {
    var backrs = _a.backrs;
    return ({
        position: 'relative',
        borderRadius: backrs.radius['radius-xl'],
        overflow: 'hidden',
        p: backrs.spacing['spacing-3xl'],
        '&::before': {
            content: '""',
            position: 'absolute',
            top: -SHIFT_UP,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "\n    linear-gradient(".concat(GRID_COLOR, " 1px, transparent 0px)\n  "),
            backgroundSize: "100% ".concat(GRID_SPACING_X + 4, "px"),
            backgroundPosition: 'top left',
            zIndex: 0,
            pointerEvents: 'none',
            WebkitMaskImage: "\n      radial-gradient(\n        ".concat(HORIZONTAL_FADE_PERCENTAGE * 2, "% ").concat(VERTICAL_FADE_PERCENTAGE * 2, "% at ").concat(FADE_HORIZONTAL_POSITION, "% 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") ").concat(FADE_START_PERCENTAGE, "%,\n        rgba(0, 0, 0, 0) ").concat(FADE_END_PERCENTAGE, "%\n      )\n    "),
            maskImage: "\n      radial-gradient(\n        ".concat(HORIZONTAL_FADE_PERCENTAGE * 2, "% ").concat(VERTICAL_FADE_PERCENTAGE * 2, "% at ").concat(FADE_HORIZONTAL_POSITION, "% 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") 0%,\n        rgba(0, 0, 0, ").concat(CENTER_OPACITY, ") ").concat(FADE_START_PERCENTAGE, "%,\n        rgba(0, 0, 0, 0) ").concat(FADE_END_PERCENTAGE, "%\n      )\n    "),
        },
        zIndex: 1,
    });
};
var backgroundTypeToStyleMap = {
    GRID: gridBackground,
    LINED: linedBackground,
    SOLID: contentContainerCommon,
};
